import React, { JSX, useContext, useEffect, useState } from "react"
import { ProviderNetworkType } from "@app/projects/enumerators";
import { ClaimsInsightsRankingRequestTypeEnum, ClaimsInsightsScore, ClaimsInsightsScoreTypeEnum } from "@app/projects/network-assessment/pci-dashboard/claims-insights";
import { PciDashboardFiltersContext } from "@app/projects/network-assessment/pci-dashboard/shared";
import { LoadingSpinner, formatDateTime } from "@app/shared";
import { DashboardTile } from "@app/shared/qes-standards";
import { capitalizeFirstLetter } from "@app/shared/utility";
import { getRoundingDisplay } from "@app/projects/network-assessment/pci-dashboard/shared/utility";
import { ColumnConfig, Waffles, printNumberAsString, wafflesKeyColumnName } from "@app/shared/waffles";
import { TextAlignment, WafflesThemes } from "@app/shared/waffles/exports";
import { Bar, Cell, ComposedChart, Legend, ResponsiveContainer, Tooltip, TooltipProps, XAxis, YAxis } from "recharts";
import { NameType, ValueType } from "recharts/types/component/DefaultTooltipContent";
import { BarRectangleItem } from "recharts/types/cartesian/Bar";

interface ClaimsInsightsProvidersTileProps {
  data: ClaimsInsightsScore[];
  effectivenessLoading: boolean;
  efficiencyLoading: boolean;
  onDashboardDetailsViewClick: (insightsCategory: ClaimsInsightsScoreTypeEnum, providerNetworkType: ProviderNetworkType, rankingRequestType: ClaimsInsightsRankingRequestTypeEnum) => void;
  providerType: ProviderNetworkType;
}

interface ClaimsInsightsProviderTableData {
  effectiveness: JSX.Element;
  efficiency: JSX.Element;
  scoreType: JSX.Element;
  [wafflesKeyColumnName]: string;
}

interface ClaimsInsightsProviderChartData {
  effectiveness: number;
  effectivenessDisplay: number;
  efficiency: number;
  efficiencyDisplay: number;
  scoreType: string;
}

// Recharts updated their event handler typing, but left out the payload
interface ClaimsInsightsRectangle extends BarRectangleItem {
  payload: {
    effectiveness: number;
    efficiency: number;
    scoreType: ClaimsInsightsScoreTypeEnum;
  }
}

const ClaimsInsightsProvidersTile = (props: ClaimsInsightsProvidersTileProps): JSX.Element => {
  const [tableData, setTableData] = useState<ClaimsInsightsProviderTableData[]>([]);
  const [chartData, setChartData] = useState<ClaimsInsightsProviderChartData[]>([]);

  const pciDashboardFiltersContext = useContext(PciDashboardFiltersContext);
  const [tooltip, setTooltip] = useState<string>();

  const getClaimsInsightsProvidersToolTip = (columnName: string): string => {
    let claimsInsightsToolTip: string;
    switch (columnName) {
      case "effectiveness":
        claimsInsightsToolTip =
          "Effectiveness is a measure of quality where 5 is the best score (higher quality) and 1 is the worst score (lower quality). Scores are relative to other providers in the same specialty and state and are derived from claims insight into 1) Mortality 2) Complication 3) Days in acute care."
        break;
      case "efficiency":
        claimsInsightsToolTip =
          "Efficiency is a measure of physician cost vs. peers where 5 is the best score (lower cost) and 1 is the worst score (higher cost). Efficiency measures are based on 1) cost per visit 2) cost per patient. Standarized pricing methodology is used, meaning efficiency is a measure of comparitive utilization and not rate/price differences."
        break;
    }
    return claimsInsightsToolTip;
  }

  const columnConfig: ColumnConfig = {
    effectiveness: {
      alignment: TextAlignment.Right,
      displayText: "Effectiveness",
      noSort: true,
      testId: "effectiveness",
      tooltip: getClaimsInsightsProvidersToolTip("effectiveness")
    },
    efficiency: {
      alignment: TextAlignment.Right,
      displayText: "Efficiency",
      noSort: true,
      testId: "efficiency",
      tooltip: getClaimsInsightsProvidersToolTip("efficiency")
    },
    scoreType: {
      alignment: TextAlignment.Left,
      displayText: "",
      noSort: true,
      testId: "score-type",
    }
  };

  useEffect(() => {
    if (props.data) {
      const maxValue: number = Math.max(...props.data.map(score => score.effectiveness).concat(props.data.map(score => score.efficiency)));
      const minAllowed: number = 0.01 * maxValue;

      const newChartData: ClaimsInsightsProviderChartData[] = props.data.map((score) => {
        return {
          effectiveness: score.effectiveness,
          effectivenessDisplay: score.effectiveness < minAllowed ? minAllowed : score.effectiveness,
          efficiency: score.efficiency,
          efficiencyDisplay: score.efficiency < minAllowed ? minAllowed : score.efficiency,
          scoreType: score.scoreType,
        }
      });

      const newTableData: ClaimsInsightsProviderTableData[] = props.data.map((score) => {
        return {
          /* eslint-disable sort-keys */
          [wafflesKeyColumnName]: score.scoreType,
          scoreType: (
            <span className="font-weight-bold" >
              {score.scoreType}
            </span>
          ),
          effectiveness: props.effectivenessLoading ? <LoadingSpinner /> : <React.Fragment>{getEffectivenessAndEfficiencyDisplay(score, "effectiveness")}</React.Fragment>,
          efficiency: props.efficiencyLoading ? <LoadingSpinner /> : <React.Fragment>{getEffectivenessAndEfficiencyDisplay(score, "efficiency")}</React.Fragment>,
          /* eslint-enable sort-keys */
        }
      });

      setChartData(newChartData.filter(row => row.scoreType != "Average Score" && row.scoreType != "Total").reverse());
      setTableData(newTableData.filter(row => row[wafflesKeyColumnName] == "Average Score" || row[wafflesKeyColumnName] == "Total").reverse());
    }
  }, [props.data, pciDashboardFiltersContext.displayedFilters]);

  const getEffectivenessAndEfficiencyDisplay = (score: ClaimsInsightsScore, key: keyof ClaimsInsightsScore) => {
    if (!score[key]) {
      return "---";
    }

    switch (score.scoreType) {
      case ClaimsInsightsScoreTypeEnum.AverageScore:
        return getRoundingDisplay((score[key] as number), false, 2);
      case ClaimsInsightsScoreTypeEnum.Total:
        return printNumberAsString(score[key] as number, "---");
      default:
        return;
    }
  }

  const CustomToolTip = ({ active, payload }: TooltipProps<ValueType, NameType>) => {
    if (!active || !tooltip)
      return null

    for (const bar of payload) {
      if (bar.dataKey === tooltip) {
        const data: ClaimsInsightsRectangle = (bar) as ClaimsInsightsRectangle;
        return (
          <div style={{ background: 'black', borderRadius: "3px", color: 'white', padding: "3px" }}>
            {data.name}
            <br />
            {data.name == "Effectiveness" ? (data.payload.effectiveness > 0 ? data.payload.effectiveness : "(No Data)") :
              (data.payload.efficiency > 0 ? data.payload.efficiency : "(No Data)")}
          </div>
        )
      }
    }
    return null
  }

  return (
    <DashboardTile
      classNames="pci-dashboard__provider-tile"
      title={capitalizeFirstLetter(props.providerType)}
      subtitle={`As of ${formatDateTime(pciDashboardFiltersContext.projectRunConfiguration?.dateCreated)}`}
      widthClass="col-md-4"
      loading={props.effectivenessLoading || props.efficiencyLoading}
    >
      <Waffles<ClaimsInsightsProviderTableData>
        columns={columnConfig}
        data={tableData}
        fullWidth={true}
        loading={props.effectivenessLoading && props.efficiencyLoading}
        testId={`claims-insights-dashboard__providers--${props.providerType}`}
        theme={WafflesThemes.Simple}
      />

      <div style={{ height: "300px", width: "100%" }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            width={500}
            height={300}
            data={chartData}
            margin={{
              bottom: 15,
              left: 1,
              right: 5,
              top: 5,
            }}
          >
            <XAxis dataKey="scoreType" label={{ position: 'bottom', value: 'SCORES' }} />
            <YAxis orientation="left" label={{ angle: -90, position: 'insideLeft', value: 'PROVIDERS' }} />
            <Tooltip cursor={false} content={<CustomToolTip />} />
            <Legend verticalAlign="top" align="right" margin={{ bottom: 20 }} fontStyle="black" formatter={(value) => <span style={{ color: 'black' }}>{value}</span>} />
            <Bar name="Effectiveness" dataKey="effectivenessDisplay" fill="#B4D4F5" onMouseOver={() => setTooltip("effectivenessDisplay")}
              onClick={(data: ClaimsInsightsRectangle) => props.onDashboardDetailsViewClick(data.payload.scoreType, props.providerType, ClaimsInsightsRankingRequestTypeEnum.Effectiveness)} >
              {props.data.filter(row => row.scoreType != "Average Score" && row.scoreType != "Total").reverse().map((score) => (
                <Cell id={`claims-activity__providers-tile--dashboard-details-${props.providerType}-${score.scoreType.toLowerCase()}-effectiveness`} key="effectiveness"
                  fill={score.effectiveness > 0 ? "#B4D4F5" : "#C0C2C4"} />
              ))}
            </Bar>
            <Bar name="Efficiency" dataKey="efficiencyDisplay" fill="#76AEEE" onMouseOver={() => setTooltip("efficiencyDisplay")}
              onClick={(data: ClaimsInsightsRectangle) => props.onDashboardDetailsViewClick(data.payload.scoreType, props.providerType, ClaimsInsightsRankingRequestTypeEnum.Efficiency)} >
              {props.data.filter(row => row.scoreType != "Average Score" && row.scoreType != "Total").reverse().map((score) => (
                <Cell id={`claims-activity__providers-tile--dashboard-details-${props.providerType}-${score.scoreType.toLowerCase()}-efficiency`} key="efficiency"
                  fill={score.efficiency > 0 ? "#76AEEE" : "#C0C2C4"} />
              ))}
            </Bar>
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </DashboardTile>
  )
}

export default ClaimsInsightsProvidersTile;